/**
 * Client Portal Page
 * Private area where clients can access their documents, service history, and request new services
 */

import { useEffect, useMemo, useState } from "react";
import styles from "./ClientPortalPage.module.css";
import { Icon } from "@/components/ui/Icon";
import { AlertModal } from "@/components/ui/AlertModal";
import { useAuth } from "@/contexts/AuthContext";
import { usersService } from "@/services/usersService";
import { workOrdersService } from "@/services/workOrdersService";
import { serviceRequestsService } from "@/services/serviceRequestsService";
import { serviceTypesService } from "@/services/serviceTypesService";
import { config } from "@/config";
import type {
  APIUser,
  APIUserDocument,
  APIWorkOrder,
  APIServiceType,
} from "@/types";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  nif: string;
}

interface Document {
  id: string;
  type: "certificate" | "workReport" | "invoice";
  title: string;
  date: string;
  serviceId?: string;
  size: string;
  status: "available" | "processing";
  fileUrl?: string;
}

interface Service {
  id: string;
  date: string;
  type: string;
  technician: string;
  status: "completed" | "scheduled" | "cancelled";
  address: string;
  description: string;
  hasCertificate: boolean;
  hasWorkReport: boolean;
  hasInvoice: boolean;
}

interface NewServiceRequest {
  type: string;
  urgency: "low" | "medium" | "high";
  address: string;
  description: string;
  preferredDate: string;
  preferredTime: string;
}

const DEFAULT_CLIENT: Client = {
  id: "",
  name: "Cliente",
  email: "",
  phone: "",
  address: "",
  nif: "",
};

const mapDocumentType = (type: string): Document["type"] => {
  const normalized = type.toLowerCase();

  if (
    normalized.includes("cert") ||
    normalized === "certificate" ||
    normalized === "certificado"
  ) {
    return "certificate";
  }

  if (
    normalized.includes("parte") ||
    normalized.includes("inspe") ||
    normalized === "workreport" ||
    normalized === "work_report"
  ) {
    return "workReport";
  }

  return "invoice";
};

const toAbsoluteFileUrl = (file: string): string => {
  if (/^https?:\/\//i.test(file)) {
    return file;
  }

  const normalizedPath = file.startsWith("/") ? file : `/${file}`;
  return `${config.api.baseUrl}${normalizedPath}`;
};

const mapAPIUserToClient = (user: APIUser): Client => ({
  id: String(user.id),
  name:
    `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
    user.company ||
    "Cliente",
  email: user.email ?? "",
  phone: user.phone_number ?? "",
  address: user.address ?? "",
  nif: user.cif ?? "",
});

const mapAPIDocument = (doc: APIUserDocument): Document => {
  const rawBaseName = (() => {
    try {
      return new URL(doc.file).pathname.split("/").pop() ?? "";
    } catch {
      return doc.file.split("/").pop() ?? "";
    }
  })();

  const title =
    doc.name ?? (rawBaseName ? decodeURIComponent(rawBaseName) : "Documento");

  const sizeBytes = doc.file_size ?? 0;
  const size =
    sizeBytes > 0
      ? sizeBytes >= 1024 * 1024
        ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(sizeBytes / 1024))} KB`
      : "";

  return {
    id: String(doc.id),
    type: mapDocumentType(doc.type),
    title,
    date: doc.created_at ?? "",
    size,
    status: doc.file ? "available" : "processing",
    fileUrl: doc.file ? toAbsoluteFileUrl(doc.file) : undefined,
  };
};

const mapWorkOrderStatus = (
  status: APIWorkOrder["status"],
): Service["status"] => {
  if (status === "completed") {
    return "completed";
  }
  if (status === "cancelled") {
    return "cancelled";
  }
  return "scheduled";
};

const normalizePreferredTime = (value: string): string | null => {
  if (!value) {
    return null;
  }

  if (value === "morning") {
    return "09:00";
  }

  if (value === "afternoon") {
    return "16:00";
  }

  return value;
};

/**
 * Client Portal Page Component
 */
export function ClientPortalPage(): React.ReactElement {
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "documents" | "services" | "request"
  >("documents");
  const [isLoading, setIsLoading] = useState(true);
  const [client, setClient] = useState<Client>(DEFAULT_CLIENT);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceTypes, setServiceTypes] = useState<APIServiceType[]>([]);
  const [isLoadingServiceTypes, setIsLoadingServiceTypes] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });

  const [newRequest, setNewRequest] = useState<NewServiceRequest>({
    type: "",
    urgency: "medium",
    address: "",
    description: "",
    preferredDate: "",
    preferredTime: "",
  });

  useEffect(() => {
    const loadServiceTypes = async () => {
      setIsLoadingServiceTypes(true);
      try {
        const response = await serviceTypesService.list({
          page: 1,
          page_size: 200,
        });
        setServiceTypes(response.results);
      } catch {
        setServiceTypes([]);
      } finally {
        setIsLoadingServiceTypes(false);
      }
    };

    void loadServiceTypes();
  }, []);

  useEffect(() => {
    const userId = Number(user?.id);
    if (!userId || Number.isNaN(userId)) {
      setIsLoading(false);
      return;
    }

    const loadPortalData = async () => {
      setIsLoading(true);
      try {
        const [userData, userDocuments, workOrdersResponse] = await Promise.all(
          [
            usersService.getById(userId),
            usersService.listDocuments(userId),
            workOrdersService.list({ page: 1, page_size: 100 }),
          ],
        );

        const mappedClient = mapAPIUserToClient(userData);
        const mappedDocuments = userDocuments.map(mapAPIDocument);
        const hasCertificate = mappedDocuments.some(
          (doc) => doc.type === "certificate",
        );
        const hasWorkReport = mappedDocuments.some(
          (doc) => doc.type === "workReport",
        );
        const hasInvoice = mappedDocuments.some(
          (doc) => doc.type === "invoice",
        );

        const mappedServices = workOrdersResponse.results.map((workOrder) => ({
          id: workOrder.order_number ?? String(workOrder.id),
          date: workOrder.scheduled_date ?? "",
          type: workOrder.plague?.name ?? "Servicio",
          technician: workOrder.assigned_technician?.name ?? "No asignado",
          status: mapWorkOrderStatus(workOrder.status),
          address: workOrder.client?.address ?? mappedClient.address,
          description:
            workOrder.plague?.name != null
              ? `Servicio para ${workOrder.plague.name}`
              : "Servicio programado",
          hasCertificate,
          hasWorkReport,
          hasInvoice,
        }));

        setClient(mappedClient);
        setDocuments(mappedDocuments);
        setServices(mappedServices);
      } catch {
        setAlertModal({
          isOpen: true,
          message: "No se pudo cargar el área de cliente.",
          type: "error",
        });
        setDocuments([]);
        setServices([]);
      } finally {
        setIsLoading(false);
      }
    };

    void loadPortalData();
  }, [user?.id]);

  useEffect(() => {
    setNewRequest((prev) => ({
      ...prev,
      address: prev.address || client.address,
    }));
  }, [client.address]);

  // Download document handler
  const handleDownloadDocument = (doc: Document) => {
    if (!doc.fileUrl) {
      setAlertModal({
        isOpen: true,
        message: "El documento aún se está procesando.",
        type: "warning",
      });
      return;
    }

    window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
    setAlertModal({
      isOpen: true,
      message: `Descargando ${doc.title}...`,
      type: "success",
    });
  };

  // Submit new service request
  const handleSubmitRequest = async () => {
    if (!newRequest.type || !newRequest.description) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa el tipo de servicio y la descripción",
        type: "warning",
      });
      return;
    }

    if (!newRequest.preferredDate) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, indica una fecha preferida",
        type: "warning",
      });
      return;
    }

    const selectedServiceTypeId = Number(newRequest.type);
    if (!selectedServiceTypeId || Number.isNaN(selectedServiceTypeId)) {
      setAlertModal({
        isOpen: true,
        message: "Selecciona un tipo de servicio válido",
        type: "warning",
      });
      return;
    }

    const selectedServiceType = serviceTypes.find(
      (serviceType) => serviceType.id === selectedServiceTypeId,
    );

    try {
      await serviceRequestsService.create({
        urgency: newRequest.urgency,
        service_type: selectedServiceTypeId,
        address: newRequest.address || client.address || null,
        preferred_date: newRequest.preferredDate,
        preferred_time: normalizePreferredTime(newRequest.preferredTime),
        description: selectedServiceType?.name
          ? `[${selectedServiceType.name}] ${newRequest.description}`
          : newRequest.description,
      });

      setAlertModal({
        isOpen: true,
        message:
          "Solicitud enviada correctamente. Nos pondremos en contacto contigo en breve.",
        type: "success",
      });

      setNewRequest({
        type: "",
        urgency: "medium",
        address: client.address,
        description: "",
        preferredDate: "",
        preferredTime: "",
      });
    } catch {
      setAlertModal({
        isOpen: true,
        message: "No se pudo enviar la solicitud. Inténtalo de nuevo.",
        type: "error",
      });
    }
  };

  const certificates = useMemo(
    () => documents.filter((doc) => doc.type === "certificate"),
    [documents],
  );
  const workReports = useMemo(
    () => documents.filter((doc) => doc.type === "workReport"),
    [documents],
  );
  const invoices = useMemo(
    () => documents.filter((doc) => doc.type === "invoice"),
    [documents],
  );

  const getDocumentIcon = (type: Document["type"]) => {
    switch (type) {
      case "certificate":
        return "shield";
      case "workReport":
        return "clipboard";
      case "invoice":
        return "fileText";
      default:
        return "document";
    }
  };

  const getDocumentTypeLabel = (type: Document["type"]) => {
    switch (type) {
      case "certificate":
        return "Certificado";
      case "workReport":
        return "Parte de Trabajo";
      case "invoice":
        return "Factura";
      default:
        return "Documento";
    }
  };

  const getServiceStatusLabel = (status: Service["status"]) => {
    switch (status) {
      case "completed":
        return "Completado";
      case "scheduled":
        return "Programado";
      case "cancelled":
        return "Cancelado";
      default:
        return status;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.welcomeSection}>
          <h1>Área Privada de Cliente</h1>
          <p className={styles.subtitle}>Bienvenido, {client.name}</p>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.clientInfo}>
            <div className={styles.infoItem}>
              <Icon name="mail" size={16} />
              <span>{client.email || "-"}</span>
            </div>
            <div className={styles.infoItem}>
              <Icon name="phone" size={16} />
              <span>{client.phone || "-"}</span>
            </div>
          </div>
          <button className={styles.logoutButton} onClick={() => void logout()}>
            <Icon name="power" size={18} />
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "documents" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("documents")}
        >
          <Icon name="download" size={20} />
          Mis Documentos
        </button>
        <button
          className={`${styles.tab} ${activeTab === "services" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("services")}
        >
          <Icon name="clipboard" size={20} />
          Histórico de Servicios
        </button>
        <button
          className={`${styles.tab} ${activeTab === "request" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("request")}
        >
          <Icon name="plus" size={20} />
          Solicitar Servicio
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Documentos Disponibles</h2>
              <p className={styles.sectionSubtitle}>
                Descarga certificados, partes de trabajo y facturas
              </p>
            </div>

            {/* Certificates Section */}
            <div className={styles.documentSection}>
              <h3 className={styles.documentSectionTitle}>
                <Icon name="shield" size={20} />
                Certificados
              </h3>
              <div className={styles.documentGrid}>
                {certificates.length === 0 && !isLoading && (
                  <p className={styles.sectionSubtitle}>
                    No hay certificados disponibles.
                  </p>
                )}
                {certificates.map((doc) => (
                  <div key={doc.id} className={styles.documentCard}>
                    <div className={styles.documentIcon}>
                      <Icon name={getDocumentIcon(doc.type)} size={32} />
                    </div>
                    <div className={styles.documentInfo}>
                      <span className={styles.documentType}>
                        {getDocumentTypeLabel(doc.type)}
                      </span>
                      <h3 className={styles.documentTitle}>{doc.title}</h3>
                      <div className={styles.documentMeta}>
                        <span>
                          <Icon name="calendar" size={14} />
                          {doc.date
                            ? new Date(doc.date).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "Sin fecha"}
                        </span>
                        <span>
                          <Icon name="document" size={14} />
                          {doc.size || "-"}
                        </span>
                      </div>
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleDownloadDocument(doc)}
                      disabled={doc.status === "processing"}
                    >
                      <Icon name="download" size={20} />
                      {doc.status === "available"
                        ? "Descargar"
                        : "Procesando..."}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Work Reports Section */}
            <div className={styles.documentSection}>
              <h3 className={styles.documentSectionTitle}>
                <Icon name="clipboard" size={20} />
                Partes de Trabajo
              </h3>
              <div className={styles.documentGrid}>
                {workReports.length === 0 && !isLoading && (
                  <p className={styles.sectionSubtitle}>
                    No hay partes de trabajo disponibles.
                  </p>
                )}
                {workReports.map((doc) => (
                  <div key={doc.id} className={styles.documentCard}>
                    <div className={styles.documentIcon}>
                      <Icon name={getDocumentIcon(doc.type)} size={32} />
                    </div>
                    <div className={styles.documentInfo}>
                      <span className={styles.documentType}>
                        {getDocumentTypeLabel(doc.type)}
                      </span>
                      <h3 className={styles.documentTitle}>{doc.title}</h3>
                      <div className={styles.documentMeta}>
                        <span>
                          <Icon name="calendar" size={14} />
                          {doc.date
                            ? new Date(doc.date).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "Sin fecha"}
                        </span>
                        <span>
                          <Icon name="document" size={14} />
                          {doc.size || "-"}
                        </span>
                      </div>
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleDownloadDocument(doc)}
                      disabled={doc.status === "processing"}
                    >
                      <Icon name="download" size={20} />
                      {doc.status === "available"
                        ? "Descargar"
                        : "Procesando..."}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Invoices Section */}
            <div className={styles.documentSection}>
              <h3 className={styles.documentSectionTitle}>
                <Icon name="fileText" size={20} />
                Facturas
              </h3>
              <div className={styles.documentGrid}>
                {invoices.length === 0 && !isLoading && (
                  <p className={styles.sectionSubtitle}>
                    No hay facturas disponibles.
                  </p>
                )}
                {invoices.map((doc) => (
                  <div key={doc.id} className={styles.documentCard}>
                    <div className={styles.documentIcon}>
                      <Icon name={getDocumentIcon(doc.type)} size={32} />
                    </div>
                    <div className={styles.documentInfo}>
                      <span className={styles.documentType}>
                        {getDocumentTypeLabel(doc.type)}
                      </span>
                      <h3 className={styles.documentTitle}>{doc.title}</h3>
                      <div className={styles.documentMeta}>
                        <span>
                          <Icon name="calendar" size={14} />
                          {doc.date
                            ? new Date(doc.date).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "Sin fecha"}
                        </span>
                        <span>
                          <Icon name="document" size={14} />
                          {doc.size || "-"}
                        </span>
                      </div>
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleDownloadDocument(doc)}
                      disabled={doc.status === "processing"}
                    >
                      <Icon name="download" size={20} />
                      {doc.status === "available"
                        ? "Descargar"
                        : "Procesando..."}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Services History Tab */}
        {activeTab === "services" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Histórico de Servicios</h2>
              <p className={styles.sectionSubtitle}>
                Consulta todos los servicios realizados
              </p>
            </div>

            <div className={styles.servicesTimeline}>
              {services.length === 0 && !isLoading && (
                <p className={styles.sectionSubtitle}>
                  No hay servicios para mostrar.
                </p>
              )}
              {services.map((service) => (
                <div key={service.id} className={styles.serviceCard}>
                  <div className={styles.serviceHeader}>
                    <div className={styles.serviceMain}>
                      <span
                        className={`${styles.serviceStatus} ${styles[service.status]}`}
                      >
                        {getServiceStatusLabel(service.status)}
                      </span>
                      <h3 className={styles.serviceType}>{service.type}</h3>
                      <p className={styles.serviceId}>ID: {service.id}</p>
                    </div>
                    <div className={styles.serviceDate}>
                      <Icon name="calendar" size={20} />
                      <span>
                        {new Date(service.date).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  <div className={styles.serviceDetails}>
                    <div className={styles.serviceDetailItem}>
                      <Icon name="users" size={16} />
                      <span>
                        <strong>Técnico:</strong> {service.technician}
                      </span>
                    </div>
                    <div className={styles.serviceDetailItem}>
                      <Icon name="mapPin" size={16} />
                      <span>
                        <strong>Dirección:</strong> {service.address}
                      </span>
                    </div>
                    <div className={styles.serviceDetailItem}>
                      <Icon name="document" size={16} />
                      <span>
                        <strong>Descripción:</strong> {service.description}
                      </span>
                    </div>
                  </div>

                  {service.status === "completed" && (
                    <div className={styles.serviceDocuments}>
                      <p className={styles.documentsLabel}>
                        Documentos disponibles:
                      </p>
                      <div className={styles.documentBadges}>
                        {service.hasCertificate && (
                          <span className={styles.documentBadge}>
                            <Icon name="shield" size={14} />
                            Certificado
                          </span>
                        )}
                        {service.hasWorkReport && (
                          <span className={styles.documentBadge}>
                            <Icon name="clipboard" size={14} />
                            Parte de Trabajo
                          </span>
                        )}
                        {service.hasInvoice && (
                          <span className={styles.documentBadge}>
                            <Icon name="fileText" size={14} />
                            Factura
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Request Service Tab */}
        {activeTab === "request" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Solicitar Nuevo Servicio</h2>
              <p className={styles.sectionSubtitle}>
                Envíanos una solicitud y nos pondremos en contacto contigo
              </p>
            </div>

            <div className={styles.requestForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Tipo de Servicio <span className={styles.required}>*</span>
                  </label>
                  <select
                    className={styles.select}
                    value={newRequest.type}
                    onChange={(e) =>
                      setNewRequest({ ...newRequest, type: e.target.value })
                    }
                    disabled={isLoadingServiceTypes}
                  >
                    <option value="">
                      {isLoadingServiceTypes
                        ? "Cargando tipos..."
                        : serviceTypes.length > 0
                          ? "Selecciona un tipo"
                          : "No hay tipos disponibles"}
                    </option>
                    {serviceTypes.map((serviceType) => (
                      <option
                        key={serviceType.id}
                        value={String(serviceType.id)}
                      >
                        {serviceType.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Urgencia</label>
                  <select
                    className={styles.select}
                    value={newRequest.urgency}
                    onChange={(e) =>
                      setNewRequest({
                        ...newRequest,
                        urgency: e.target.value as NewServiceRequest["urgency"],
                      })
                    }
                  >
                    <option value="low">Baja - Mantenimiento rutinario</option>
                    <option value="medium">Media - En los próximos días</option>
                    <option value="high">Alta - Urgente</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Dirección del Servicio</label>
                <input
                  type="text"
                  className={styles.input}
                  value={newRequest.address}
                  onChange={(e) =>
                    setNewRequest({ ...newRequest, address: e.target.value })
                  }
                  placeholder="Dirección donde se realizará el servicio"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Fecha Preferida <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    className={styles.input}
                    value={newRequest.preferredDate}
                    onChange={(e) =>
                      setNewRequest({
                        ...newRequest,
                        preferredDate: e.target.value,
                      })
                    }
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Hora Preferida</label>
                  <select
                    className={styles.select}
                    value={newRequest.preferredTime}
                    onChange={(e) =>
                      setNewRequest({
                        ...newRequest,
                        preferredTime: e.target.value,
                      })
                    }
                  >
                    <option value="">Sin preferencia</option>
                    <option value="09:00">Mañana (8:00 - 14:00)</option>
                    <option value="16:00">Tarde (14:00 - 20:00)</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Descripción del Problema{" "}
                  <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={newRequest.description}
                  onChange={(e) =>
                    setNewRequest({
                      ...newRequest,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe detalladamente el problema o la incidencia..."
                  rows={5}
                />
              </div>

              <div className={styles.urgencyInfo}>
                <Icon name="alertTriangle" size={20} />
                <div>
                  <strong>Nota sobre urgencias:</strong>
                  <p>
                    Para emergencias fuera de horario, llama al{" "}
                    <a href="tel:+34900123456">900 123 456</a> (24h)
                  </p>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  className={styles.submitButton}
                  onClick={handleSubmitRequest}
                >
                  <Icon name="check" size={20} />
                  Enviar Solicitud
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* GDPR Notice */}
      <div className={styles.gdprNotice}>
        <Icon name="shield" size={16} />
        <span>
          Tus datos están protegidos según el RGPD. Consulta nuestra{" "}
          <a href="/privacy">Política de Privacidad</a>.
        </span>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() =>
          setAlertModal({ isOpen: false, message: "", type: "info" })
        }
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}

export default ClientPortalPage;
